// 组件全部使用的情报的， 这里先对studio这边能控制的部分做配置化，情报的组件不做配置化改动
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'umi';
import classnames from 'classnames';
import CommonFilter from '@intelligence/components/CommonFilter';
import useSetQueryDefinition from '@intelligence/hooks/useQueryDefinition';
import useFilterTagsOptions from '@intelligence/hooks/useFilterTagsOptions';
import { OPTIONS_NAMESPACE } from '@intelligence/models/options';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  DEFAULTFILTERVALUE as defaultFilterValue,
  FILTERKEYLIST as filterKeyList,
  DEFAULTINPUTLIST as defaultInputList,
  DEFAULT_METRICS_PLAYED_FILTER_VALUE,
  DEFAULT_METRICS_WISHLIST_FILTER_VALUE,
  SEGMENT_METRIC_FILTER_MAP,
  SEGMENT_FILTER_BUTTON_TEXT,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/constants';
import {
  OVERLAP_STEAM_ORDERMETRIC,
  OVERLAP_STEAM_METRICPARAMS,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/constants/metrics';
import {
  getCountryOptionConfig,
  getCurrentValidMarket,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/utils';
import { handleOverlapSteamGameDetailFilterParamsToApi as handleFilterToApiParams } from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { SortType } from '@intelligence/pages/TopCharts/_config/enums';
import { StudioContext } from '@home/pages/studio/context';
import { createGameBasicinfoFetcher } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/services';
import { condParams } from '@intelligence/_config/condParams';
import { getSteamOverlapRegionCountryLevelConfig } from '@intelligence/services/overlapAnalysis';
import useMarketFilterApi, { MarketSpecailType } from '@intelligence/hooks/useMarketSelector';
import { RegionType } from '@intelligence/components/RegionCountrySelector/components/RegionTypeSelector';
import './index.scss';
import { getDefaultFilterValue, PAGE_NAME_TO_DEFAULT_SEGMENT } from '../../constants';

interface IProps {
  // 子组件
  children: React.ReactNode;
  // 描述信息的info提示中英文
  tipsEn?: string;
  tipsCn?: string;
  className?: string;
  style?: React.CSSProperties;
  // 描述话术中英文
  describeEn?: string;
  describeCn?: string;
  defaultPage? : string;
}

const PageWrap = (props: IProps) => {
  const { children, className, style, defaultPage } = props;
  useSetQueryDefinition({
    filterTags: condParams.filterTags,
  });
  // 当前游戏信息
  const { currentGame } = useContext(StudioContext);
  const { steamTagOptions } = useFilterTagsOptions();

  const { unified_edition_id: unifiedEditionId } = currentGame || {};

  const { regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.metricTable,
  });

  const [marketsListMap, setMarketsListMap] = useState<{ played: any[]; wishlisted: any[]; }>({
    played: [],
    wishlisted: [],
  });

  // 筛选器参数
  const [filterVal, setFilterVal] = useState<any>(getDefaultFilterValue(defaultPage));

  const defaultSegmentKey = PAGE_NAME_TO_DEFAULT_SEGMENT[defaultPage ?? 'played'] || 'played_play';

  // 转换的请求参数
  const [fetchParams, setFetchParams] = useState({
    order: 'desc',
    order_metric: OVERLAP_STEAM_ORDERMETRIC[defaultSegmentKey],
    page_name: defaultPage ?? 'played',
    page: 1,
    page_size: 50,
    engagement_level_a: '2h',
    engagement_level_b: '2h',
    region: 'global',
    metrics: OVERLAP_STEAM_METRICPARAMS[defaultSegmentKey],
    metric_filter: SEGMENT_METRIC_FILTER_MAP[defaultSegmentKey] || DEFAULT_METRICS_PLAYED_FILTER_VALUE,
    id: unifiedEditionId,
  });

  // 当前 4-segment key，传递给子组件用于查找 metrics 映射
  const [segmentKey, setSegmentKey] = useState<string>(defaultSegmentKey);

  useEffect(() => {
    setFetchParams((prev: any) => {
      if (prev?.id === unifiedEditionId) return prev;
      return {
        ...prev,
        id: unifiedEditionId,
      };
    });
  }, [unifiedEditionId]);

  const [genreLoading, setGenreLoading] = useState<boolean>(true);
  const [gameInfoLoading, setGameInfoLoading] = useState<boolean>(true);

  // 子组件加载状态，用于筛选器防频繁切换
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);

  // 筛选器防频繁切换：当接口请求进行中时禁用筛选器交互
  const isFilterDisabled = tableLoading || summaryLoading;

  // 子组件上报 loading 状态的回调
  const onTableLoadingChange = useCallback((loading: boolean) => {
    setTableLoading(loading);
  }, []);
  const onSummaryLoadingChange = useCallback((loading: boolean) => {
    setSummaryLoading(loading);
  }, []);

  // 根据 segmentKey 获取 REVENUE_GROWTH 的默认值
  const getSegmentRevenueGrowthDefault = (sk: string) => {
    const [metricFilter] = SEGMENT_METRIC_FILTER_MAP[sk] || [];
    return {
      opt: metricFilter?.opt || '>',
      value: metricFilter?.value ?? 20,
    };
  };

  // segment 切换时，自动更新 REVENUE_GROWTH 筛选器的默认值
  const handleChangeExtra = (key: string, value?: any, newValue?: Record<string, any>) => {
    if (key === filterKeyMap.OVERLAPSTEAM_PLAYERS && newValue) {
      const currentSegment = filterVal?.[filterKeyMap.OVERLAPSTEAM_PLAYERS]?.value;
      const nextSegment = value?.value || 'played_play';
      const currentRevenueGrowth = filterVal?.[filterKeyMap.REVENUE_GROWTH]?.value;
      const currentSegmentDefault = getSegmentRevenueGrowthDefault(currentSegment || 'played_play');
      const isUsingCurrentSegmentDefault =
        !currentRevenueGrowth?.opt
        || currentRevenueGrowth?.value === ''
        || (
          currentRevenueGrowth?.opt === currentSegmentDefault.opt
          && currentRevenueGrowth?.value === currentSegmentDefault.value
        );

      if (isUsingCurrentSegmentDefault) {
        // eslint-disable-next-line no-param-reassign
        newValue[filterKeyMap.REVENUE_GROWTH] = {
          value: getSegmentRevenueGrowthDefault(nextSegment),
        };
      }
    }
  };

  const marketsOptions = useMemo(() => {
    return getCountryOptionConfig(
      marketsListMap?.[fetchParams.page_name as 'played' | 'wishlisted'],
      regionOptions,
    );
  }, [marketsListMap, regionOptions, fetchParams.page_name]);

  // 动态设置国家 options 和 REVENUE_GROWTH 的 buttonText
  const currentDefaultInputList: any = useMemo(() => {
    return defaultInputList.map((item: any) => {
      if (item.key === filterKeyMap.REGION_COUNTRY_SELECTOR) {
        return {
          ...item,
          countryOptions: marketsOptions,
        };
      }
      if (item.key === filterKeyMap.REVENUE_GROWTH) {
        return {
          ...item,
          buttonText: SEGMENT_FILTER_BUTTON_TEXT[segmentKey] || 'dbi_overlapsteam_played_play_percent',
          placeholderText: 'dbi_overlapsteam_filter_placeholder',
        };
      }
      return item;
    });
  }, [marketsOptions, segmentKey]);

  const onConfirm = (value: any) => {
    // 防止在数据加载期间触发新的请求
    if (isFilterDisabled) return;

    const paramsTemp = handleFilterToApiParams(fetchParams, value);
    const { region_type: currentRegionType, region, page_name: pageName } = paramsTemp;
    let currentValidRegion = region;
    if (currentRegionType === RegionType.country) {
      currentValidRegion = getCurrentValidMarket(
        marketsListMap?.[pageName as 'played' | 'wishlisted'],
        region,
      );
    }

    // 获取当前选中的 4-segment key
    const currentSegmentKey = value?.overlapSteam_players?.value || 'played_play';
    const newFormValue = {
      ...fetchParams,
      ...paramsTemp,
      region: currentValidRegion,
      order_metric: OVERLAP_STEAM_ORDERMETRIC[currentSegmentKey],
      metrics: OVERLAP_STEAM_METRICPARAMS[currentSegmentKey],
      order: SortType.Desc,
      page: 1,
      page_size: 50,
      id: unifiedEditionId,
    };
    setSegmentKey(currentSegmentKey);
    setFetchParams(newFormValue);
  };

  const handleSteamTagClick = (type: string, tag: string) => {
    if (type !== filterKeyMap.STEAM_TAGS || !tag || isFilterDisabled) return;

    const currentSteamTagFilter = filterVal?.[filterKeyMap.STEAM_TAGS] || defaultFilterValue[filterKeyMap.STEAM_TAGS];
    const currentFilterValue = {
      ...filterVal,
      [filterKeyMap.STEAM_TAGS]: {
        ...currentSteamTagFilter,
        value: [tag],
      },
    };

    setFilterVal(currentFilterValue);
    onConfirm(currentFilterValue);
  };

  // 获取品类全量options数据
  const dispatch = useDispatch();
  const getGenreAll = async () => {
    setGenreLoading(true);
    try {
      await dispatch({
        type: `${OPTIONS_NAMESPACE}/getGenresOptions`,
        payload: { entityType: 'combine' },
      });
    } catch (error) {
      setGenreLoading(false);
    }
    setGenreLoading(false);
  };

  useEffect(() => {
    getGenreAll();
  }, []);

  const filterCfg = {
    filterKeyList,
    regionFilterParams: {},
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
    },
    showConfirmButton: false,
    isCustomLayoutFilter: true,
    specialParams: {
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      entityType: 'pc',
      showSelectType: false,
      rangeTimeOtherStyle: true,
      steamTagOptions,
    },
  };

  useEffect(() => {
    let isMounted = true;

    if (!unifiedEditionId) {
      setMarketsListMap({ played: [], wishlisted: [] });
      return () => {
        isMounted = false;
      };
    }

    getSteamOverlapRegionCountryLevelConfig({ id: unifiedEditionId })
      .then((res) => {
        if (!isMounted) return;
        const playedData = res?.data?.find((item: any) => item?.page_name === 'played');
        const wishlistedData = res?.data?.find((item: any) => item?.page_name === 'wishlisted');
        setMarketsListMap({
          played: playedData?.markets || [],
          wishlisted: wishlistedData?.markets || [],
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setMarketsListMap({ played: [], wishlisted: [] });
      });

    return () => {
      isMounted = false;
    };
  }, [unifiedEditionId]);

  // 游戏没上线，设置默认为wishlisted
  useEffect(() => {
    setGameInfoLoading(true);
    const {
      game_type: gameType = 'pc',
      unified_edition_id: currentUnifiedEditionId,
    } = currentGame || {};

    const baseParams: any = {
      entity_type: gameType,
      id: currentUnifiedEditionId,
      app_id: [],
      region_type: 'region',
      regions: ['global'],
    };

    createGameBasicinfoFetcher(gameType)(baseParams)
      .then((data) => {
        const isRelease = data?.data?.basic_info?.is_release;
        const pageName = isRelease ? 'played' : 'wishlisted';
        const newSegmentKey = PAGE_NAME_TO_DEFAULT_SEGMENT[defaultPage ?? pageName] || (isRelease ? 'played_play' : 'wishlisted_play');
        if (!isRelease) {
          setFilterVal((prev: any) => ({
            ...prev,
            overlapSteam_players: {
              value: defaultPage ? (PAGE_NAME_TO_DEFAULT_SEGMENT[defaultPage] || defaultPage) : newSegmentKey,
            },
            // 同步更新 REVENUE_GROWTH，使筛选器 UI 和 handleFilterToApiParams 都能读到正确的默认值
            [filterKeyMap.REVENUE_GROWTH]: {
              value: getSegmentRevenueGrowthDefault(newSegmentKey),
            },
            revenueGrowthTwo: {
              value: {
                ...prev?.revenueGrowthTwo?.value,
                segmentName: isRelease ? 'played' : 'wishlisted',
              },
            },
          }));
          setSegmentKey(newSegmentKey);
          setFetchParams((prev) => ({
            ...prev,
            id: currentUnifiedEditionId,
            page_name: defaultPage ?? pageName,
            order_metric: OVERLAP_STEAM_ORDERMETRIC[newSegmentKey],
            metrics: OVERLAP_STEAM_METRICPARAMS[newSegmentKey],
            metric_filter: SEGMENT_METRIC_FILTER_MAP[newSegmentKey] || DEFAULT_METRICS_WISHLIST_FILTER_VALUE,
          }));
        }
      })
      .finally(() => setGameInfoLoading(false));
  }, [currentGame?.game_type, currentGame?.unified_edition_id, defaultPage]);

  return (
    <div
      className={classnames('dbh-potential-market', className)}
      style={style}
    >
      <div className="dbh-potential-market-header">
        {/* 筛选器 - 数据加载中时禁用交互，防止频繁切换 */}
        <div className={`dbh-potential-market-filter${isFilterDisabled ? ' dbh-potential-market-filter-disabled' : ''}`}>
          <CommonFilter
            filterConfirm={onConfirm}
            filterValue={filterVal}
            setFilterValue={setFilterVal}
            filterCfg={filterCfg}
            defaultInputList={currentDefaultInputList}
            onChangeExtra={handleChangeExtra}
            isBasicLoading={gameInfoLoading}
          />
        </div>
      </div>

      <div className="dbh-potential-market-content">
        {children &&
          React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                filterVal: fetchParams,
                segmentKey,
                genreLoading,
                gameInfoLoading,
                onTagClick: handleSteamTagClick,
                onTableLoadingChange,
                onSummaryLoadingChange,
              });
            }
            return child;
          })}
      </div>
    </div>
  );
};

export default PageWrap;
